import { useState, useMemo, useEffect, useCallback } from "react";
import { DateHelper } from "@bryntum/schedulerpro";
import { useConfirm } from "@omit/react-confirm-dialog";
import { toast } from "sonner";
import { Skeleton } from "@/modern/components/ui/skeleton";
import { Button } from "@/modern/components/ui/button";
import { Checkbox } from "@/modern/components/ui/checkbox";
import { cn } from "@/modern/utils";
import { useEntityFileContext } from "@/react/components/entityViews/files/context/EntityFileContext";
import type { SwivuFile } from "@/react/components/entityViews/files/types/swivuFile";
import { PresignedFilePreview } from "@/react/components/entityViews/files/components/PresignedFilePreview";
import InfiniteScroll from "@/modern/components/infinite-scroll";
import { Loader2 } from "lucide-react";
import { FileActionsMenu } from "@/react/components/entityViews/files/components/FileActionsMenu";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { useFileSpotIncentive } from "@/react/components/entityViews/files/hooks/useFileSpotIncentive";
import { useEntityFileUpload } from "@/react/components/entityViews/files/hooks/useEntityFileUpload";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { getFormattedFileSize } from "@/other/utils/utils";
import type { FileViewerMode } from "./FileDetailModal/types";
import {
  FileGridToolbar,
  type FileBrowserFilterState,
  type FileTypeTab,
} from "./FileGridToolbar";
import { groupFilesByUploadDay } from "@/react/components/entityViews/files/utils/groupFilesByUploadDay";
import { FileDayGroupHeader } from "./FileDayGroupHeader";
import { FileMediaUploaderMeta } from "./FileMediaUploaderMeta";

type ViewMode = "grid" | "list";

function parseFileBrowserViewMode(value: string | undefined | null): ViewMode {
  return value === "list" ? "list" : "grid";
}

/** Cap zoom so Largest is still a multi-cell density, not a fake single-image viewer. */
const ZOOM_LEVELS = [
  { cols: 6, label: "Smallest" },
  { cols: 5, label: "Small" },
  { cols: 4, label: "Medium" },
  { cols: 3, label: "Large" },
  { cols: 2, label: "Larger" },
];

interface FileGridViewProps {
  /** Controlled browser filters (TaskFilesEditor). When set, list is server-filtered. */
  filters?: FileBrowserFilterState;
  onFiltersChange?: (next: FileBrowserFilterState) => void;
  onSearchChange?: (search: string) => void;
  /** @deprecated Prefer `filters.fileTypeTab` — kept for callers that only drive type tab */
  activeFilter?: FileTypeTab;
  onFilterChange?: (f: FileTypeTab) => void;
  onNavigationFilesChange?: (files: SwivuFile[]) => void;
  /** Opt-in day section headers (Task Dash Media manage). Off for Documents. */
  groupByDay?: boolean;
  /** Compact command chrome (Task Media / Documents manage). */
  compactToolbar?: boolean;
  /** CompanyCam-style multi-select with bulk download / delete (Task Media). */
  enableMultiSelect?: boolean;
}

export const FileGridView = ({
  filters: controlledFilters,
  onFiltersChange,
  onSearchChange,
  activeFilter: legacyFilter,
  onFilterChange,
  onNavigationFilesChange,
  groupByDay = false,
  compactToolbar = false,
  enableMultiSelect = false,
}: FileGridViewProps = {}) => {
  const {
    files,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoadingFiles,
    isErrorFiles,
    refetch,
    handleFileOpen,
    handleFileDownload,
    deleteFile,
    invalidateEntityFiles,
    entityRecord,
    uploadFiles,
    isImportingCompanyCam,
    currentCCProject,
  } = useEntityFileContext();
  const confirm = useConfirm();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [isSelectionBusy, setIsSelectionBusy] = useState(false);
  /** File ids with an in-flight delete — tile shows a loader instead of the preview. */
  const [deletingIds, setDeletingIds] = useState<Set<number>>(() => new Set());

  const { project, editorHandler, userPreferences } = useSwivuBryntumContext();

  const [internalFilters, setInternalFilters] = useState<FileBrowserFilterState>({
    fileTypeTab: legacyFilter ?? "media",
    source: "all",
    datePreset: "any",
    search: "",
    ordering: "-uploaded_at",
  });

  const isControlled = controlledFilters !== undefined;
  const filters = isControlled ? controlledFilters : internalFilters;

  const setFilters = useCallback(
    (next: FileBrowserFilterState) => {
      if (isControlled) {
        onFiltersChange?.(next);
        onFilterChange?.(next.fileTypeTab);
      } else {
        setInternalFilters(next);
      }
    },
    [isControlled, onFiltersChange, onFilterChange],
  );

  const setSearch = useCallback(
    (search: string) => {
      if (onSearchChange) {
        onSearchChange(search);
        return;
      }
      setFilters({ ...filters, search });
    },
    [onSearchChange, setFilters, filters],
  );

  const [viewMode, setViewMode] = useState<ViewMode>(() =>
    parseFileBrowserViewMode(
      userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.fileBrowserViewMode, "grid"),
    ),
  );

  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);
      userPreferences?.updateSetting(PERSISTENCE_STATE_KEYS.fileBrowserViewMode, mode);
    },
    [userPreferences],
  );

  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    const stored = localStorage.getItem("fileGridZoomLevel");
    const parsed = stored ? parseInt(stored, 10) : 1;
    return Math.min(Math.max(parsed, 0), ZOOM_LEVELS.length - 1);
  });

  const uploadCategory = filters.fileTypeTab === "documents" ? "documents" : "media";
  const { uploadRef, isUploading, openUpload, handleUpload, accept } = useEntityFileUpload({
    category: uploadCategory,
    uploadNewFile: uploadFiles,
  });

  const currentCols = ZOOM_LEVELS[zoomLevel].cols;

  const companyCamProjectId = entityRecord.companyCamProject as number | undefined;
  const companyCamName =
    currentCCProject?.name ??
    (companyCamProjectId ? `Project ${companyCamProjectId}` : null);

  const companyCamImportHint =
    filters.fileTypeTab === "documents"
      ? "Documents will appear here shortly."
      : filters.fileTypeTab === "media"
        ? "Media will appear here shortly."
        : "Files will appear here shortly.";

  const { shouldShowAdjustPoints } = useFileSpotIncentive({
    entityRecord,
    project,
    editorHandler,
  });

  // Controlled path trusts server filters. Uncontrolled path still client-filters by type
  // (source/date/search/ordering require provider remount via controlled TaskFilesEditor).
  const filteredFiles = useMemo(() => {
    if (isControlled) return files;
    switch (filters.fileTypeTab) {
      case "media":
        return files.filter((f) => f.fileType === "image");
      case "documents":
        return files.filter((f) => f.fileType === "document");
      default:
        return files;
    }
  }, [isControlled, filters.fileTypeTab, files]);

  useEffect(() => {
    onNavigationFilesChange?.(filteredFiles);
  }, [filteredFiles, onNavigationFilesChange]);

  const dayGroups = useMemo(
    () => (groupByDay ? groupFilesByUploadDay(filteredFiles) : null),
    [groupByDay, filteredFiles],
  );

  const filteredIdSet = useMemo(
    () => new Set(filteredFiles.map((file) => file.id)),
    [filteredFiles],
  );

  // Drop selection entries that left the current filtered set (tab/search/delete).
  useEffect(() => {
    if (!enableMultiSelect) return;
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      let changed = false;
      const next = new Set<number>();
      for (const id of prev) {
        if (filteredIdSet.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [enableMultiSelect, filteredIdSet]);

  useEffect(() => {
    if (!enableMultiSelect) setSelectedIds(new Set());
  }, [enableMultiSelect]);

  const openWithMode = useCallback(
    (file: SwivuFile, mode: FileViewerMode = "default") => {
      handleFileOpen(file, { mode });
    },
    [handleFileOpen],
  );

  const toggleFileSelected = useCallback((fileId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const getDaySelectionState = useCallback(
    (dayFiles: SwivuFile[]): boolean | "indeterminate" => {
      if (dayFiles.length === 0) return false;
      let selectedCount = 0;
      for (const file of dayFiles) {
        if (selectedIds.has(file.id)) selectedCount += 1;
      }
      if (selectedCount === 0) return false;
      if (selectedCount === dayFiles.length) return true;
      return "indeterminate";
    },
    [selectedIds],
  );

  const toggleDaySelection = useCallback((dayFiles: SwivuFile[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = dayFiles.every((file) => next.has(file.id));
      if (allSelected) {
        for (const file of dayFiles) next.delete(file.id);
      } else {
        for (const file of dayFiles) next.add(file.id);
      }
      return next;
    });
  }, []);

  const handleBulkDownload = useCallback(async () => {
    const selected = filteredFiles.filter((file) => selectedIds.has(file.id));
    if (selected.length === 0) return;
    setIsSelectionBusy(true);
    let started = 0;
    try {
      for (const file of selected) {
        if (await handleFileDownload(file)) started += 1;
      }
      if (selected.length === 1) return;
      if (started === selected.length) {
        toast.success(`Started download for ${started} files.`, { duration: 3000 });
      } else if (started > 0) {
        toast.warning(`Started ${started} of ${selected.length} downloads. Some failed.`, {
          duration: 6000,
        });
      }
    } finally {
      setIsSelectionBusy(false);
    }
  }, [filteredFiles, handleFileDownload, selectedIds]);

  const handleBulkDelete = useCallback(async () => {
    const selected = filteredFiles.filter((file) => selectedIds.has(file.id));
    if (selected.length === 0) return;

    const confirmed = await confirm({
      title: selected.length === 1 ? "Confirm Delete" : `Delete ${selected.length} photos?`,
      description:
        selected.length === 1
          ? `Are you sure you want to delete "${selected[0]?.name ?? "this photo"}"?`
          : `Are you sure you want to delete ${selected.length} selected photos? This cannot be undone.`,
      customActions: ({
        confirm: confirmAction,
        cancel,
      }: {
        confirm: () => void;
        cancel: () => void;
      }) => (
        <div className="space-x-4">
          <Button variant="ghost" onClick={cancel}>
            Cancel
          </Button>
          <Button
            className="bg-red-100 text-red-800 transition-colors hover:bg-red-200 dark:bg-red-900 dark:text-red-200 hover:dark:bg-red-950 hover:dark:text-red-300"
            onClick={confirmAction}
          >
            Delete
          </Button>
        </div>
      ),
    });
    if (!confirmed) return;

    setIsSelectionBusy(true);
    setDeletingIds(new Set(selected.map((file) => file.id)));
    clearSelection();
    try {
      // Parallel deletes; silent so we toast / invalidate once after all settle.
      const results = await Promise.allSettled(
        selected.map((file) =>
          deleteFile(file.id, { silent: true }).finally(() => {
            setDeletingIds((prev) => {
              const next = new Set(prev);
              next.delete(file.id);
              return next;
            });
          }),
        ),
      );
      const deleted = results.filter((result) => result.status === "fulfilled").length;
      if (deleted === selected.length) {
        toast.success(deleted === 1 ? "Photo deleted." : `${deleted} photos deleted.`, {
          duration: 3000,
        });
      } else if (deleted > 0) {
        toast.warning(`Deleted ${deleted} of ${selected.length} photos. Some failed.`, {
          duration: 6000,
        });
      } else {
        toast.error(
          selected.length === 1
            ? "Failed to delete photo. Please try again."
            : `Failed to delete ${selected.length} photos. Please try again.`,
          { duration: 6000 },
        );
      }
      // Heal offset windows / count across every filter for this entity.
      if (deleted > 0) {
        void invalidateEntityFiles();
      }
    } finally {
      setIsSelectionBusy(false);
      setDeletingIds(new Set());
    }
  }, [clearSelection, confirm, deleteFile, filteredFiles, invalidateEntityFiles, selectedIds]);

  const zoomIn = () => {
    if (zoomLevel < ZOOM_LEVELS.length - 1) {
      const newLevel = zoomLevel + 1;
      setZoomLevel(newLevel);
      localStorage.setItem("fileGridZoomLevel", newLevel.toString());
    }
  };

  const zoomOut = () => {
    if (zoomLevel > 0) {
      const newLevel = zoomLevel - 1;
      setZoomLevel(newLevel);
      localStorage.setItem("fileGridZoomLevel", newLevel.toString());
    }
  };

  const renderActions = (file: SwivuFile) => (
    <FileActionsMenu
      file={file}
      onFileDownload={handleFileDownload}
      onFileDelete={(fileId) => deleteFile(fileId)}
      onView={(f) => openWithMode(f)}
      onAnnotate={(f) => openWithMode(f, "annotate")}
      onAnalyze={(f) => openWithMode(f, "analyze")}
      onPromote={(f) => openWithMode(f, "promote")}
      onAdjustPoints={(f) => openWithMode(f, "adjustPoints")}
      onSummaryShare={(f) => openWithMode(f, "summaryShare")}
      onExtract={(f) => openWithMode(f, "extract")}
      shouldShowAdjustPoints={shouldShowAdjustPoints}
    />
  );

  const hasActiveSelection = enableMultiSelect && selectedIds.size > 0;
  /** Task Media manage: uploader + time-of-day beat filenames at browse density. */
  const showMediaUploaderMeta = groupByDay || enableMultiSelect;

  const renderGridTile = (file: SwivuFile) => {
    const isSelected = enableMultiSelect && selectedIds.has(file.id);
    const isDeleting = deletingIds.has(file.id);
    return (
      <div key={file.id} className="group relative">
        <div
          onClick={() => {
            if (isDeleting) return;
            if (hasActiveSelection) {
              toggleFileSelected(file.id);
              return;
            }
            openWithMode(file);
          }}
          className={cn(
            "relative block overflow-hidden rounded-2xl border bg-muted/20",
            isDeleting ? "cursor-wait" : "cursor-pointer",
            isSelected ? "border-primary ring-2 ring-primary/35" : "border-border/60",
          )}
          aria-busy={isDeleting || undefined}
        >
          <div className="relative isolate aspect-square w-full overflow-hidden">
            {isDeleting ? (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-muted text-muted-foreground"
                aria-label={`Deleting ${file.name}`}
              >
                <Loader2 className="size-6 animate-spin" aria-hidden />
                <span className="text-[10px]">Deleting...</span>
              </div>
            ) : (
              <PresignedFilePreview
                file={file}
                className="absolute inset-0 size-full object-cover"
                fileIconClassName="size-16"
                fileIconContainerClassName="absolute inset-0 size-full"
                compactLoading
                isThumbnail
              />
            )}
          </div>

          {enableMultiSelect && !isDeleting ? (
            <div
              className="absolute left-2 top-2 z-30"
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                checked={isSelected}
                disabled={isSelectionBusy}
                onCheckedChange={() => toggleFileSelected(file.id)}
                aria-label={`Select ${file.name}`}
                className="border-white/80 bg-background/90 shadow-sm data-[state=checked]:border-primary"
              />
            </div>
          ) : null}

          {showMediaUploaderMeta && !isDeleting ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-30 bg-gradient-to-t from-black/65 via-black/35 to-transparent px-2.5 pb-2.5 pt-8">
              <FileMediaUploaderMeta file={file} variant="onPhoto" />
            </div>
          ) : null}

          {!isDeleting ? (
            <div
              className={cn(
                "absolute inset-0 z-20 flex flex-col rounded-2xl bg-black/70 p-3 transition-opacity duration-200",
                // Keep the bottom clear so uploader meta stays visible under the dim overlay.
                showMediaUploaderMeta && "pb-12",
                hasActiveSelection ? "opacity-0" : "opacity-0 group-hover:opacity-100",
                showMediaUploaderMeta ? "justify-start" : "justify-between",
              )}
            >
              <div className="flex justify-end">{renderActions(file)}</div>
              {!showMediaUploaderMeta ? (
                <div className="truncate text-sm font-normal text-white" title={file.name}>
                  {file.name}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const renderListRow = (file: SwivuFile) => {
    const isSelected = enableMultiSelect && selectedIds.has(file.id);
    const isDeleting = deletingIds.has(file.id);
    return (
      <div
        key={file.id}
        role="button"
        tabIndex={isDeleting ? -1 : 0}
        onClick={() => {
          if (isDeleting) return;
          if (hasActiveSelection) {
            toggleFileSelected(file.id);
            return;
          }
          openWithMode(file);
        }}
        onKeyDown={(e) => {
          if (isDeleting) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (hasActiveSelection) toggleFileSelected(file.id);
            else openWithMode(file);
          }
        }}
        className={cn(
          "group flex w-full items-center gap-3 rounded-2xl border bg-muted/20 p-3 text-left transition-colors duration-150 ease-out",
          isDeleting ? "cursor-wait opacity-90" : "cursor-pointer hover:bg-muted/40",
          isSelected ? "border-primary ring-2 ring-primary/35" : "border-border/60",
        )}
        aria-busy={isDeleting || undefined}
      >
        {enableMultiSelect && !isDeleting ? (
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              disabled={isSelectionBusy}
              onCheckedChange={() => toggleFileSelected(file.id)}
              aria-label={`Select ${file.name}`}
              className="shrink-0"
            />
          </div>
        ) : null}
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl">
          {isDeleting ? (
            <div
              className="flex h-12 w-12 items-center justify-center bg-muted text-muted-foreground"
              aria-label={`Deleting ${file.name}`}
            >
              <Loader2 className="size-5 animate-spin" aria-hidden />
            </div>
          ) : (
            <PresignedFilePreview
              file={file}
              className="h-12 w-12 object-cover"
              fileIconClassName="size-6"
              fileIconContainerClassName="h-12 w-12"
              compactLoading
              isThumbnail
              showLoadingLabel={false}
            />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {showMediaUploaderMeta ? (
            <>
              <FileMediaUploaderMeta file={file} variant="inline" />
              {file.source === "companycam" ? (
                <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <i className="fa-solid fa-camera text-[10px]" />
                    CompanyCam
                  </span>
                </div>
              ) : null}
            </>
          ) : (
            <>
              <p className="truncate text-sm font-normal text-foreground" title={file.name}>
                {file.name || "Untitled"}
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                {file.sizeBytes ? <span>{getFormattedFileSize(file.sizeBytes)}</span> : null}
                {file.createdAt && (
                  <span>{DateHelper.format(new Date(file.createdAt), "MM/DD/YYYY")}</span>
                )}
                {file.source === "companycam" && (
                  <span className="flex items-center gap-1">
                    <i className="fa-solid fa-camera text-[10px]" />
                    CompanyCam
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        {!isDeleting ? (
          <div
            className="flex-shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
            onClick={(e) => e.stopPropagation()}
          >
            {renderActions(file)}
          </div>
        ) : null}
      </div>
    );
  };

  const fetchMoreFooter = (
    <div className="flex justify-center py-4">
      {isFetchingNextPage && <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />}
    </div>
  );

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <FileGridToolbar
        filters={filters}
        onFiltersChange={setFilters}
        onSearchChange={setSearch}
        viewMode={viewMode}
        onViewModeChange={handleViewModeChange}
        zoomLevel={zoomLevel}
        zoomMax={ZOOM_LEVELS.length - 1}
        zoomLabel={ZOOM_LEVELS[zoomLevel].label}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        isUploading={isUploading}
        onUpload={openUpload}
        uploadAcceptLabel={filters.fileTypeTab === "documents" ? "documents" : "media"}
        uploadRef={uploadRef}
        accept={accept}
        onUploadChange={handleUpload}
        variant={compactToolbar ? "compact" : "default"}
        selection={
          enableMultiSelect
            ? {
                selectedCount: selectedIds.size,
                onClear: clearSelection,
                onDownload: () => {
                  void handleBulkDownload();
                },
                onDelete: () => {
                  void handleBulkDelete();
                },
                isBusy: isSelectionBusy,
              }
            : null
        }
      />

      <div className="min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto">
      {isLoadingFiles && filteredFiles.length === 0 && (
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="space-y-2">
              <Skeleton className="h-32 w-full rounded-2xl" />
              <Skeleton className="h-4 w-3/4 rounded-lg" />
            </div>
          ))}
        </div>
      )}

      {isErrorFiles && (
        <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
          <p className="text-sm font-normal text-muted-foreground">Failed to load files.</p>
          <Button size="sm" variant="outline" className="rounded-full font-normal" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      )}

      {!isLoadingFiles && !isErrorFiles && filteredFiles.length === 0 && (
        isImportingCompanyCam ? (
          <div className="flex flex-col items-center justify-center gap-1.5 px-6 py-10 text-center">
            <div className="flex max-w-lg items-center justify-center gap-2 text-sm font-normal text-muted-foreground">
              <Loader2 className="size-4 shrink-0 animate-spin opacity-80" aria-hidden />
              <span className="min-w-0">
                {companyCamName
                  ? `Importing from CompanyCam project ${companyCamName}…`
                  : "Importing from CompanyCam project…"}
              </span>
            </div>
            <p className="text-xs font-normal text-muted-foreground/70">{companyCamImportHint}</p>
          </div>
        ) : (
          <div className="py-10 text-center text-sm font-normal text-muted-foreground">No files found</div>
        )
      )}

      {filteredFiles.length > 0 && (
        <>
          {viewMode === "grid" ? (
            <InfiniteScroll hasMore={hasNextPage} isLoading={isFetchingNextPage} next={fetchNextPage} threshold={1}>
              {dayGroups ? (
                <div className="min-w-0 space-y-6">
                  {dayGroups.map((group) => (
                    <section
                      key={group.dayKey}
                      className="min-w-0"
                      aria-label={`${group.label}, ${group.files.length} photos`}
                    >
                      <FileDayGroupHeader
                        label={group.label}
                        count={group.files.length}
                        selectionChecked={
                          enableMultiSelect ? getDaySelectionState(group.files) : undefined
                        }
                        onSelectionChange={
                          enableMultiSelect
                            ? () => toggleDaySelection(group.files)
                            : undefined
                        }
                        selectionDisabled={isSelectionBusy}
                      />
                      <div
                        className="grid min-w-0 gap-3"
                        style={{ gridTemplateColumns: `repeat(${currentCols}, minmax(0, 1fr))` }}
                      >
                        {group.files.map(renderGridTile)}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div
                  className="grid gap-3"
                  style={{ gridTemplateColumns: `repeat(${currentCols}, minmax(0, 1fr))` }}
                >
                  {filteredFiles.map(renderGridTile)}
                </div>
              )}
              {(isFetchingNextPage || hasNextPage) && fetchMoreFooter}
            </InfiniteScroll>
          ) : (
            <div className="space-y-2">
              <InfiniteScroll hasMore={hasNextPage} isLoading={isFetchingNextPage} next={fetchNextPage} threshold={1}>
                {dayGroups ? (
                  <div className="min-w-0 space-y-6">
                    {dayGroups.map((group) => (
                      <section
                        key={group.dayKey}
                        aria-label={`${group.label}, ${group.files.length} photos`}
                        className="min-w-0 space-y-2"
                      >
                        <FileDayGroupHeader
                          label={group.label}
                          count={group.files.length}
                          selectionChecked={
                            enableMultiSelect ? getDaySelectionState(group.files) : undefined
                          }
                          onSelectionChange={
                            enableMultiSelect
                              ? () => toggleDaySelection(group.files)
                              : undefined
                          }
                          selectionDisabled={isSelectionBusy}
                        />
                        {group.files.map(renderListRow)}
                      </section>
                    ))}
                  </div>
                ) : (
                  filteredFiles.map(renderListRow)
                )}
                {(isFetchingNextPage || hasNextPage) && (
                  <div className="flex justify-center py-8">
                    {isFetchingNextPage && (
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    )}
                  </div>
                )}
              </InfiniteScroll>
            </div>
          )}
        </>
      )}
      </div>
    </div>
  );
};
